// ./src/components/features/Forms/FormFieldsEditModal/FormFieldsEditModal.tsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Stack,
  Text,
  TextInput,
  Textarea,
  NumberInput,
  Switch,
  Group,
  Badge,
  ActionIcon,
  Paper,
  ScrollArea,
  Box,
  Accordion,
} from "@mantine/core";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useNavigate } from "react-router-dom";
import { FormField, FormFieldInput, Form } from "@/utils/api";
import {
  AppIcon,
  AppButton,
  AppDrawer,
  LoadingIndicator,
} from "@/components/generic";
import { useFormFields } from "@/hooks";
import { FIELD_TYPE_IDS } from "@/config";
import { FormFieldTypeSelector } from "../FormFieldTypeSelector";
import { FormFieldTypeBadge } from "../FormFieldTypeBadge";
import { GibberishCheckSelector } from "../GibberishCheckSelector";

// Field option types and editors
import {
  parseFieldOptions,
  stringifyFieldOptions,
  PhoneFieldOptions,
  DateFieldOptions,
  BirthdateFieldOptions,
  FileFieldOptions,
  MultipleChoiceFieldOptions,
  CreditCardFieldOptions,
  LookupFieldOptions,
  RecaptchaEnterpriseOptions,
  NumberFieldOptions,
  EmailFieldOptions,
  UrlFieldOptions,
  UuidFieldOptions,
  HexColorFieldOptions,
  PasswordFieldOptions,
  UsernameFieldOptions,
  SlugFieldOptions,
  CoordinatesFieldOptions,
} from "./fieldOptionsTypes";
import { PhoneFieldOptionsEditor } from "./PhoneFieldOptionsEditor";
import { DateFieldOptionsEditor } from "./DateFieldOptionsEditor";
import { FileFieldOptionsEditor } from "./FileFieldOptionsEditor";
import { MultipleChoiceFieldOptionsEditor } from "./MultipleChoiceFieldOptionsEditor";
import { CreditCardFieldOptionsEditor } from "./CreditCardFieldOptionsEditor";
import { LookupFieldOptionsEditor } from "./LookupFieldOptionsEditor";
import { RecaptchaEnterpriseFieldOptionsEditor } from "./RecaptchaEnterpriseFieldOptionsEditor";
import { BirthdateFieldOptionsEditor } from "./BirthdateFieldOptionsEditor";
import { NumberFieldOptionsEditor } from "./NumberFieldOptionsEditor";
import { EmailFieldOptionsEditor } from "./EmailFieldOptionsEditor";
import { UrlFieldOptionsEditor } from "./UrlFieldOptionsEditor";
import { UuidFieldOptionsEditor } from "./UuidFieldOptionsEditor";
import { HexColorFieldOptionsEditor } from "./HexColorFieldOptionsEditor";
import { PasswordFieldOptionsEditor } from "./PasswordFieldOptionsEditor";
import { UsernameFieldOptionsEditor } from "./UsernameFieldOptionsEditor";
import { SlugFieldOptionsEditor } from "./SlugFieldOptionsEditor";
import { CoordinatesFieldOptionsEditor } from "./CoordinatesFieldOptionsEditor";
import { OPERATION_RIGHTS } from "@/config";

export interface FormFieldsEditModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Form to edit fields for */
  form?: Form | null;

  /** Form ID (when form object not available) */
  formId?: string | null;
}

interface FieldFormData {
  id?: string;
  // Basic
  name: string;
  label: string;
  fieldType: number | null;
  required: boolean;
  status: number;
  // Validation
  minValue: number | null;
  maxValue: number | null;
  customErrorMessage: string;
  // Type-specific options (stored as JSON in options field)
  phoneOptions: PhoneFieldOptions;
  dateOptions: DateFieldOptions;
  birthdateOptions: BirthdateFieldOptions;
  fileOptions: FileFieldOptions;
  multipleChoiceOptions: MultipleChoiceFieldOptions;
  creditCardOptions: CreditCardFieldOptions;
  lookupOptions: LookupFieldOptions;
  recaptchaEnterpriseOptions: RecaptchaEnterpriseOptions;
  numberOptions: NumberFieldOptions;
  emailOptions: EmailFieldOptions;
  urlOptions: UrlFieldOptions;
  uuidOptions: UuidFieldOptions;
  hexColorOptions: HexColorFieldOptions;
  passwordOptions: PasswordFieldOptions;
  usernameOptions: UsernameFieldOptions;
  slugOptions: SlugFieldOptions;
  coordinatesOptions: CoordinatesFieldOptions;
  // Uniqueness
  maxUniqueValues: number | null;
  messageOnUniqueness: string;
  // Advanced
  orderNo: number | null;
  defaultValue: string;
  isHidden: boolean;
  isReplyTo: boolean;
  regexPattern: string;
  gibberishCheck: string | null;
}

const initialFieldData: FieldFormData = {
  // Basic
  name: "",
  label: "",
  fieldType: FIELD_TYPE_IDS.SHORT_TEXT,
  required: false,
  status: 100,
  // Validation
  minValue: null,
  maxValue: null,
  customErrorMessage: "",
  // Type-specific options
  phoneOptions: {},
  dateOptions: {},
  birthdateOptions: {},
  fileOptions: {},
  multipleChoiceOptions: { values: [] },
  creditCardOptions: { maskNumber: true },
  lookupOptions: {},
  recaptchaEnterpriseOptions: {},
  numberOptions: { numberType: "decimal" },
  emailOptions: {},
  urlOptions: {},
  uuidOptions: {},
  hexColorOptions: {},
  passwordOptions: {},
  usernameOptions: {},
  slugOptions: {},
  coordinatesOptions: {},
  // Uniqueness
  maxUniqueValues: null,
  messageOnUniqueness: "",
  // Advanced
  orderNo: null,
  defaultValue: "",
  isHidden: false,
  isReplyTo: false,
  regexPattern: "",
  gibberishCheck: null,
};

// Field types that support min/max validation (excluding NUMBER which now has its own editor)
const SUPPORTS_MIN_MAX = [FIELD_TYPE_IDS.SHORT_TEXT, FIELD_TYPE_IDS.LONG_TEXT];

// Field types that support gibberish check override
const SUPPORTS_GIBBERISH_CHECK = [
  FIELD_TYPE_IDS.SHORT_TEXT,
  FIELD_TYPE_IDS.LONG_TEXT,
];

// Field types that can be reply-to (email types)
const CAN_BE_REPLY_TO = [FIELD_TYPE_IDS.EMAIL];

// Helper to get options editor based on field type
// Update getFieldTypeCategory function
const getFieldTypeCategory = (
  fieldType: number | null
):
  | "phone"
  | "email"
  | "url"
  | "date"
  | "birthdate"
  | "file"
  | "select"
  | "multipleChoice"
  | "creditCard"
  | "lookup"
  | "recaptchaEnterprise"
  | "number"
  | "regex"
  | "uuid"
  | "hexColor"
  | "password"
  | "username"
  | "slug"
  | "coordinates"
  | "minMax"
  | "none" => {
  if (fieldType === null) return "none";

  switch (fieldType) {
    case FIELD_TYPE_IDS.EMAIL:
      return "email";
    case FIELD_TYPE_IDS.URL:
      return "url";
    case FIELD_TYPE_IDS.PHONE:
      return "phone";
    case FIELD_TYPE_IDS.DATE:
      return "date";
    case FIELD_TYPE_IDS.BIRTHDATE:
      return "birthdate";
    case FIELD_TYPE_IDS.FILE:
      return "file";
    case FIELD_TYPE_IDS.MULTIPLE_CHOICE:
      return "multipleChoice";
    case FIELD_TYPE_IDS.CREDIT_CARD:
      return "creditCard";
    case FIELD_TYPE_IDS.LOOKUP:
      return "lookup";
    case FIELD_TYPE_IDS.RECAPTCHA_ENTERPRISE:
      return "recaptchaEnterprise";
    case FIELD_TYPE_IDS.NUMBER:
      return "number";
    case FIELD_TYPE_IDS.REGULAR_EXPRESSION:
      return "regex";
    case FIELD_TYPE_IDS.UUID:
      return "uuid";
    case FIELD_TYPE_IDS.HEX_COLOR:
      return "hexColor";
    case FIELD_TYPE_IDS.PASSWORD:
      return "password";
    case FIELD_TYPE_IDS.USERNAME:
      return "username";
    case FIELD_TYPE_IDS.SLUG:
      return "slug";
    case FIELD_TYPE_IDS.COORDINATES:
      return "coordinates";
    default:
      if (SUPPORTS_MIN_MAX.includes(fieldType)) return "minMax";
      return "none";
  }
};

export const FormFieldsEditModal: React.FC<FormFieldsEditModalProps> = ({
  opened,
  onClose,
  form,
  formId,
}) => {
  const navigate = useNavigate();
  const effectiveFormId = form?.id || formId;

  // Use the form fields hook
  const {
    formFields,
    loading,
    isCreating,
    isUpdating,
    removingFormFieldId,
    list,
    create,
    update,
    remove,
    lastError,
    clearError,
  } = useFormFields({
    autoLoad: false,
    isAuthenticated: true,
  });

  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [fieldFormData, setFieldFormData] =
    useState<FieldFormData>(initialFieldData);
  const [validationErrors, setValidationErrors] = useState<any>({});

  // Optimistic reordering state
  const [savingFieldId, setSavingFieldId] = useState<string | null>(null);
  const [localFields, setLocalFields] = useState<FormField[]>([]);
  const justReorderedRef = useRef(false);

  // Sync localFields with formFields from hook (skip after reorder)
  useEffect(() => {
    if (!savingFieldId) {
      if (justReorderedRef.current) {
        // Skip sync after successful reorder - we already have correct order
        justReorderedRef.current = false;
        return;
      }
      setLocalFields(formFields);
    }
  }, [formFields, savingFieldId]);

  // Load fields when modal opens
  useEffect(() => {
    if (opened && effectiveFormId) {
      clearError();
      list(
        { formId: [effectiveFormId] },
        { page: 1, pageSize: 100 },
        [{ name: "orderNo", order: "ASC" }],
        true
      );
    }
  }, [opened, effectiveFormId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!opened) {
      setEditingFieldId(null);
      setIsAddingNew(false);
      setFieldFormData(initialFieldData);
      setValidationErrors({});
      setSavingFieldId(null);
      setLocalFields([]);
      justReorderedRef.current = false;
    }
  }, [opened]);

  // Determine which features are available based on field type
  const fieldType = fieldFormData.fieldType;
  const fieldCategory = getFieldTypeCategory(fieldType);
  const showReplyTo = fieldType !== null && CAN_BE_REPLY_TO.includes(fieldType);
  const showGibberishCheck =
    fieldType !== null && SUPPORTS_GIBBERISH_CHECK.includes(fieldType);

  const canEditFields = form?.rights?.includes(OPERATION_RIGHTS.UPDATE);

  const handleFieldChange = (field: keyof FieldFormData, value: any) => {
    setFieldFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (validationErrors[field]) {
      setValidationErrors((prev: any) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Handle field type change - reset type-specific fields
  const handleFieldTypeChange = (typeId: string | null) => {
    const newFieldType = typeId ? Number(typeId) : null;

    setFieldFormData((prev) => ({
      ...prev,
      fieldType: newFieldType,
      // Reset reply-to if new type doesn't support it
      isReplyTo:
        newFieldType !== null && CAN_BE_REPLY_TO.includes(newFieldType)
          ? prev.isReplyTo
          : false,
    }));

    if (validationErrors.fieldType) {
      setValidationErrors((prev: any) => ({
        ...prev,
        fieldType: undefined,
      }));
    }
  };

  const validateFieldForm = (): boolean => {
    const errors: any = {};

    if (!fieldFormData.name.trim()) {
      errors.name = "Field name is required";
    }

    if (!fieldFormData.label.trim()) {
      errors.label = "Field label is required";
    }

    if (fieldFormData.fieldType === null) {
      errors.fieldType = "Field type is required";
    }

    // Validate min/max relationship
    if (
      fieldFormData.minValue !== null &&
      fieldFormData.maxValue !== null &&
      fieldFormData.minValue > fieldFormData.maxValue
    ) {
      errors.maxValue = "Max must be ≥ min";
    }

    // Validate options for multiple choice field
    if (
      fieldCategory === "multipleChoice" &&
      (!fieldFormData.multipleChoiceOptions.values ||
        fieldFormData.multipleChoiceOptions.values.length === 0)
    ) {
      errors.multipleChoiceOptions = "At least one option is required";
    }

    // Validate regex pattern
    if (fieldCategory === "regex") {
      const regexValidation = validateRegexPattern(fieldFormData.regexPattern);
      if (!regexValidation.valid) {
        errors.regexPattern = regexValidation.error;
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Get options JSON based on field type
  const getOptionsJson = (): string | null => {
    switch (fieldCategory) {
      case "email":
        return stringifyFieldOptions(fieldFormData.emailOptions);
      case "url":
        return stringifyFieldOptions(fieldFormData.urlOptions);
      case "phone":
        return stringifyFieldOptions(fieldFormData.phoneOptions);
      case "date":
        return stringifyFieldOptions(fieldFormData.dateOptions);
      case "birthdate":
        return stringifyFieldOptions(fieldFormData.birthdateOptions);
      case "file":
        return stringifyFieldOptions(fieldFormData.fileOptions);
      case "multipleChoice":
        return stringifyFieldOptions(fieldFormData.multipleChoiceOptions);
      case "creditCard":
        return stringifyFieldOptions(fieldFormData.creditCardOptions);
      case "lookup":
        return stringifyFieldOptions(fieldFormData.lookupOptions);
      case "recaptchaEnterprise":
        return stringifyFieldOptions(fieldFormData.recaptchaEnterpriseOptions);
      case "number":
        return stringifyFieldOptions(fieldFormData.numberOptions);
      case "uuid":
        return stringifyFieldOptions(fieldFormData.uuidOptions);
      case "hexColor":
        return stringifyFieldOptions(fieldFormData.hexColorOptions);
      case "password":
        return stringifyFieldOptions(fieldFormData.passwordOptions);
      case "username":
        return stringifyFieldOptions(fieldFormData.usernameOptions);
      case "slug":
        return stringifyFieldOptions(fieldFormData.slugOptions);
      case "coordinates":
        return stringifyFieldOptions(fieldFormData.coordinatesOptions);
      case "regex":
        return fieldFormData.regexPattern.trim() || null;
      default:
        return null;
    }
  };

  // Helper to validate regex pattern string (supports pattern or pattern/flags format)
  const validateRegexPattern = (
    pattern: string
  ): { valid: boolean; error?: string } => {
    if (!pattern || pattern.trim() === "") {
      return { valid: false, error: "Regular expression pattern is required" };
    }

    try {
      // Check if pattern includes flags (e.g., "pattern/im")
      const lastSlashIndex = pattern.lastIndexOf("/");
      if (lastSlashIndex > 0) {
        const regexBody = pattern.substring(0, lastSlashIndex);
        const flags = pattern.substring(lastSlashIndex + 1);
        // Validate flags contain only valid characters
        if (flags && !/^[gimsuy]*$/.test(flags)) {
          return {
            valid: false,
            error: `Invalid flags: ${flags}. Valid flags are: g, i, m, s, u, y`,
          };
        }
        new RegExp(regexBody, flags);
      } else {
        new RegExp(pattern);
      }
      return { valid: true };
    } catch (e) {
      const errorMessage =
        e instanceof Error ? e.message : "Invalid regular expression";
      return { valid: false, error: errorMessage };
    }
  };

  const handleAddField = () => {
    // Calculate next order number based on localFields
    const maxOrder = localFields.reduce(
      (max, f) => Math.max(max, f.orderNo || 0),
      0
    );

    setIsAddingNew(true);
    setEditingFieldId(null);
    setFieldFormData({
      ...initialFieldData,
      orderNo: maxOrder + 1000,
    });
    setValidationErrors({});
  };

  const handleEditField = (field: FormField) => {
    setIsAddingNew(false);
    setEditingFieldId(field.id || null);

    // Parse options based on field type
    const category = getFieldTypeCategory(field.fieldType ?? null);

    setFieldFormData({
      id: field.id,
      // Basic
      name: field.name || "",
      label: field.label || "",
      fieldType: field.fieldType ?? null,
      required: field.required ?? false,
      status: field.status ?? 100,
      // Validation
      minValue: field.minValue ?? null,
      maxValue: field.maxValue ?? null,
      customErrorMessage: field.customErrorMessage || "",
      // Type-specific options
      phoneOptions:
        category === "phone"
          ? parseFieldOptions<PhoneFieldOptions>(field.options) || {}
          : {},
      dateOptions:
        category === "date"
          ? parseFieldOptions<DateFieldOptions>(field.options) || {}
          : {},
      birthdateOptions:
        category === "birthdate"
          ? parseFieldOptions<BirthdateFieldOptions>(field.options) || {}
          : {},
      fileOptions:
        category === "file"
          ? parseFieldOptions<FileFieldOptions>(field.options) || {}
          : {},
      multipleChoiceOptions:
        category === "multipleChoice"
          ? parseFieldOptions<MultipleChoiceFieldOptions>(field.options) || {
              values: [],
            }
          : { values: [] },
      creditCardOptions:
        category === "creditCard"
          ? parseFieldOptions<CreditCardFieldOptions>(field.options) || {
              maskNumber: true,
            }
          : { maskNumber: true },
      lookupOptions:
        category === "lookup"
          ? parseFieldOptions<LookupFieldOptions>(field.options) || {}
          : {},
      recaptchaEnterpriseOptions:
        category === "recaptchaEnterprise"
          ? parseFieldOptions<RecaptchaEnterpriseOptions>(field.options) || {}
          : {},
      numberOptions:
        category === "number"
          ? parseFieldOptions<NumberFieldOptions>(field.options) || {
              numberType: "decimal",
            }
          : { numberType: "decimal" },
      emailOptions:
        category === "email"
          ? parseFieldOptions<EmailFieldOptions>(field.options) || {}
          : {},
      urlOptions:
        category === "url"
          ? parseFieldOptions<UrlFieldOptions>(field.options) || {}
          : {},
      uuidOptions:
        category === "uuid"
          ? parseFieldOptions<UuidFieldOptions>(field.options) || {}
          : {},
      hexColorOptions:
        category === "hexColor"
          ? parseFieldOptions<HexColorFieldOptions>(field.options) || {}
          : {},
      passwordOptions:
        category === "password"
          ? parseFieldOptions<PasswordFieldOptions>(field.options) || {}
          : {},
      usernameOptions:
        category === "username"
          ? parseFieldOptions<UsernameFieldOptions>(field.options) || {}
          : {},
      slugOptions:
        category === "slug"
          ? parseFieldOptions<SlugFieldOptions>(field.options) || {}
          : {},
      coordinatesOptions:
        category === "coordinates"
          ? parseFieldOptions<CoordinatesFieldOptions>(field.options) || {}
          : {},
      // Uniqueness
      maxUniqueValues: field.maxUniqueValues ?? null,
      messageOnUniqueness: field.messageOnUniqueness || "",
      // Advanced
      orderNo: field.orderNo ?? null,
      defaultValue: field.defaultValue || "",
      isHidden: field.isHidden ?? false,
      isReplyTo: field.isReplyTo ?? false,
      regexPattern: category === "regex" ? field.options || "" : "",
      gibberishCheck: field.gibberishCheck ?? null,
    });
    setValidationErrors({});
  };

  const handleCancelEdit = () => {
    setIsAddingNew(false);
    setEditingFieldId(null);
    setFieldFormData(initialFieldData);
    setValidationErrors({});
  };

  const handleSaveField = async () => {
    if (!validateFieldForm() || !effectiveFormId) return;

    // Determine if this field type uses min/max
    const usesMinMax =
      fieldCategory === "minMax" ||
      fieldCategory === "number" ||
      fieldCategory === "phone" ||
      fieldCategory === "password" ||
      fieldCategory === "username" ||
      fieldCategory === "multipleChoice" ||
      fieldCategory === "slug";

    const fieldData: FormFieldInput = {
      formId: effectiveFormId,
      // Basic
      name: fieldFormData.name.trim(),
      label: fieldFormData.label.trim(),
      fieldType: fieldFormData.fieldType,
      required: fieldFormData.required,
      status: fieldFormData.status,
      // Validation
      minValue: usesMinMax ? fieldFormData.minValue : null,
      maxValue: usesMinMax ? fieldFormData.maxValue : null,
      options: getOptionsJson(),
      customErrorMessage: fieldFormData.customErrorMessage.trim() || null,
      // Uniqueness
      maxUniqueValues: fieldFormData.maxUniqueValues,
      messageOnUniqueness: fieldFormData.messageOnUniqueness.trim() || null,
      // Advanced
      orderNo: fieldFormData.orderNo,
      defaultValue: fieldFormData.defaultValue.trim() || null,
      isHidden: fieldFormData.isHidden,
      isReplyTo: showReplyTo ? fieldFormData.isReplyTo : false,
      gibberishCheck: showGibberishCheck ? fieldFormData.gibberishCheck : null,
    };

    let result: FormField | null = null;

    if (isAddingNew) {
      result = await create(fieldData);
    } else if (editingFieldId) {
      result = await update(editingFieldId, fieldData);
    }

    if (result) {
      handleCancelEdit();
    }
  };

  const handleDeleteField = async (fieldId: string) => {
    const success = await remove(fieldId);
    if (success && editingFieldId === fieldId) {
      handleCancelEdit();
    }
  };

  // Optimistic reorder handler
  const handleOrderChange = useCallback(
    async (
      draggedItem: FormField,
      itemBefore: FormField | undefined,
      itemAfter: FormField | undefined,
      newLocalOrder: FormField[]
    ) => {
      if (!draggedItem.id || !effectiveFormId || !canEditFields) return;

      // Optimistically update local state immediately
      setLocalFields(newLocalOrder);
      setSavingFieldId(draggedItem.id);

      try {
        let newOrderNo: number;

        if (itemBefore && itemAfter) {
          const beforeOrderNo = itemBefore.orderNo || 0;
          const afterOrderNo = itemAfter.orderNo || 0;
          newOrderNo = beforeOrderNo + (afterOrderNo - beforeOrderNo) / 2;
        } else if (itemAfter) {
          const afterOrderNo = itemAfter.orderNo || 1000;
          newOrderNo = afterOrderNo / 2;
        } else if (itemBefore) {
          const beforeOrderNo = itemBefore.orderNo || 0;
          newOrderNo = beforeOrderNo + 1000;
        } else {
          newOrderNo = 1000;
        }

        const timestampDecimal = (Date.now() % 1000) / 1000;
        const finalOrderNo = newOrderNo + timestampDecimal;

        // Update the dragged item's orderNo in local state
        setLocalFields((prev) =>
          prev.map((f) =>
            f.id === draggedItem.id ? { ...f, orderNo: finalOrderNo } : f
          )
        );

        await update(draggedItem.id, {
          orderNo: finalOrderNo,
        });

        // Mark that we just reordered so the sync effect skips
        justReorderedRef.current = true;
      } catch (error) {
        // On error, revert to server state
        justReorderedRef.current = false;
        setLocalFields(formFields);
      } finally {
        setSavingFieldId(null);
      }
    },
    [update, effectiveFormId, canEditFields, formFields]
  );

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) return;

    // Create new order array
    const newOrder = Array.from(localFields);
    const [draggedItem] = newOrder.splice(sourceIndex, 1);
    newOrder.splice(destinationIndex, 0, draggedItem);

    // Determine neighbors in the NEW position
    const itemBefore =
      destinationIndex > 0 ? newOrder[destinationIndex - 1] : undefined;
    const itemAfter =
      destinationIndex < newOrder.length - 1
        ? newOrder[destinationIndex + 1]
        : undefined;

    handleOrderChange(draggedItem, itemBefore, itemAfter, newOrder);
  };

  const handleEditForm = () => {
    if (effectiveFormId) {
      navigate(`/forms/${effectiveFormId}/edit`);
    }
  };

  // Get min/max labels based on field type
  const getMinMaxLabels = () => {
    if (
      fieldType === FIELD_TYPE_IDS.SHORT_TEXT ||
      fieldType === FIELD_TYPE_IDS.LONG_TEXT ||
      fieldType === FIELD_TYPE_IDS.PHONE
    ) {
      return { min: "Min Length", max: "Max Length" };
    }
    return { min: "Min Value", max: "Max Value" };
  };

  const minMaxLabels = getMinMaxLabels();

  const renderFieldSummary = (field: FormField) => {
    const isEditing = editingFieldId === field.id;
    const isSaving = savingFieldId === field.id;

    return (
      <Group justify="space-between" wrap="nowrap" w="100%">
        <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          {canEditFields && (
            <Box
              style={{
                cursor: isSaving ? "not-allowed" : "grab",
                display: "flex",
                alignItems: "center",
                padding: "4px",
                borderRadius: "4px",
                backgroundColor: "transparent",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => {
                if (!isSaving) {
                  e.currentTarget.style.backgroundColor =
                    "var(--mantine-color-gray-1)";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              {isSaving ? (
                <LoadingIndicator size={20} />
              ) : (
                <AppIcon iconName="GripVertical" size={20} color="gray" />
              )}
            </Box>
          )}
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Group gap="xs" wrap="nowrap">
              <Text fw={500} truncate>
                {field.label || field.name || "Untitled Field"}
              </Text>
              {field.required && (
                <Badge size="xs" variant="light" color="red">
                  Required
                </Badge>
              )}
              {field.isHidden && (
                <Badge size="xs" variant="light" color="gray">
                  Hidden
                </Badge>
              )}
              {field.isReplyTo && (
                <Badge size="xs" variant="light" color="blue">
                  Reply-To
                </Badge>
              )}
            </Group>
            <Group gap="xs">
              <Text size="xs" c="dimmed" truncate>
                {field.name}
              </Text>
              <FormFieldTypeBadge type={field.fieldType} size="xs" />
              {field.maxUniqueValues && (
                <Badge size="xs" variant="outline" color="grape">
                  Unique
                </Badge>
              )}
            </Group>
          </Box>
        </Group>
        <Group gap="xs" wrap="nowrap">
          {!isEditing && canEditFields && (
            <>
              <ActionIcon
                variant="subtle"
                color="blue"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditField(field);
                }}
                disabled={isSaving}
              >
                <AppIcon iconName="Edit" size={16} />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                color="red"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteField(field.id || "");
                }}
                loading={removingFormFieldId === field.id}
                disabled={isSaving}
              >
                <AppIcon iconName="Trash" size={16} />
              </ActionIcon>
            </>
          )}
        </Group>
      </Group>
    );
  };

  // Render type-specific options editor
  const renderTypeSpecificOptions = () => {
    switch (fieldCategory) {
      case "email":
        return (
          <EmailFieldOptionsEditor
            value={fieldFormData.emailOptions}
            onChange={(opts) => handleFieldChange("emailOptions", opts)}
          />
        );
      case "url":
        return (
          <UrlFieldOptionsEditor
            value={fieldFormData.urlOptions}
            onChange={(opts) => handleFieldChange("urlOptions", opts)}
          />
        );
      case "phone":
        return (
          <PhoneFieldOptionsEditor
            value={fieldFormData.phoneOptions}
            onChange={(opts) => handleFieldChange("phoneOptions", opts)}
            minValue={fieldFormData.minValue}
            maxValue={fieldFormData.maxValue}
            onMinValueChange={(val) => handleFieldChange("minValue", val)}
            onMaxValueChange={(val) => handleFieldChange("maxValue", val)}
            maxValueError={validationErrors.maxValue}
          />
        );
      case "date":
        return (
          <DateFieldOptionsEditor
            value={fieldFormData.dateOptions}
            onChange={(opts) => handleFieldChange("dateOptions", opts)}
            isBirthdate={fieldType === FIELD_TYPE_IDS.BIRTHDATE}
          />
        );
      case "birthdate":
        return (
          <BirthdateFieldOptionsEditor
            value={fieldFormData.birthdateOptions}
            onChange={(opts) => handleFieldChange("birthdateOptions", opts)}
          />
        );
      case "file":
        return (
          <FileFieldOptionsEditor
            value={fieldFormData.fileOptions}
            onChange={(opts) => handleFieldChange("fileOptions", opts)}
          />
        );

      case "multipleChoice":
        return (
          <MultipleChoiceFieldOptionsEditor
            value={fieldFormData.multipleChoiceOptions}
            onChange={(opts) =>
              handleFieldChange("multipleChoiceOptions", opts)
            }
            minValue={fieldFormData.minValue}
            maxValue={fieldFormData.maxValue}
            onMinValueChange={(val) => handleFieldChange("minValue", val)}
            onMaxValueChange={(val) => handleFieldChange("maxValue", val)}
            error={validationErrors.multipleChoiceOptions}
          />
        );
      case "creditCard":
        return (
          <CreditCardFieldOptionsEditor
            value={fieldFormData.creditCardOptions}
            onChange={(opts) => handleFieldChange("creditCardOptions", opts)}
          />
        );
      case "lookup":
        return (
          <LookupFieldOptionsEditor
            value={fieldFormData.lookupOptions}
            onChange={(opts) => handleFieldChange("lookupOptions", opts)}
          />
        );
      case "recaptchaEnterprise":
        return (
          <RecaptchaEnterpriseFieldOptionsEditor
            value={fieldFormData.recaptchaEnterpriseOptions}
            onChange={(opts) =>
              handleFieldChange("recaptchaEnterpriseOptions", opts)
            }
          />
        );
      case "number":
        return (
          <NumberFieldOptionsEditor
            value={fieldFormData.numberOptions}
            onChange={(opts) => handleFieldChange("numberOptions", opts)}
            minValue={fieldFormData.minValue}
            maxValue={fieldFormData.maxValue}
            onMinValueChange={(val) => handleFieldChange("minValue", val)}
            onMaxValueChange={(val) => handleFieldChange("maxValue", val)}
            maxValueError={validationErrors.maxValue}
          />
        );
      case "regex":
        return (
          <Stack gap="sm">
            <Textarea
              label="Regular Expression Pattern"
              placeholder="e.g., ^[A-Z]{2,3}-\d{4}$ or ^[a-z]+$/i"
              value={fieldFormData.regexPattern}
              onChange={(e) =>
                handleFieldChange("regexPattern", e.target.value)
              }
              error={validationErrors.regexPattern}
              description="Enter pattern, optionally with flags after / (e.g., pattern/im)"
              minRows={2}
              maxRows={4}
              autosize
              size="sm"
              required
              styles={{
                input: {
                  fontFamily: "monospace",
                },
              }}
            />
            <Text size="xs" c="dimmed">
              Examples: <code>^\d{5}$</code> (5 digits),{" "}
              <code>^[A-Za-z]+$/i</code> (letters, case-insensitive)
            </Text>
          </Stack>
        );
      case "minMax":
        return (
          <Stack gap="sm">
            <Group grow>
              <NumberInput
                label={minMaxLabels.min}
                placeholder="No min"
                value={fieldFormData.minValue ?? ""}
                onChange={(value) =>
                  handleFieldChange("minValue", value === "" ? null : value)
                }
                min={0}
                size="sm"
              />
              <NumberInput
                label={minMaxLabels.max}
                placeholder="No max"
                value={fieldFormData.maxValue ?? ""}
                onChange={(value) =>
                  handleFieldChange("maxValue", value === "" ? null : value)
                }
                error={validationErrors.maxValue}
                min={fieldFormData.minValue ?? 0}
                size="sm"
              />
            </Group>
          </Stack>
        );
      case "uuid":
        return (
          <UuidFieldOptionsEditor
            value={fieldFormData.uuidOptions}
            onChange={(opts) => handleFieldChange("uuidOptions", opts)}
          />
        );

      case "hexColor":
        return (
          <HexColorFieldOptionsEditor
            value={fieldFormData.hexColorOptions}
            onChange={(opts) => handleFieldChange("hexColorOptions", opts)}
          />
        );

      case "password":
        return (
          <PasswordFieldOptionsEditor
            value={fieldFormData.passwordOptions}
            onChange={(opts) => handleFieldChange("passwordOptions", opts)}
            minValue={fieldFormData.minValue}
            maxValue={fieldFormData.maxValue}
            onMinValueChange={(val) => handleFieldChange("minValue", val)}
            onMaxValueChange={(val) => handleFieldChange("maxValue", val)}
            maxValueError={validationErrors.maxValue}
          />
        );

      case "username":
        return (
          <UsernameFieldOptionsEditor
            value={fieldFormData.usernameOptions}
            onChange={(opts) => handleFieldChange("usernameOptions", opts)}
            minValue={fieldFormData.minValue}
            maxValue={fieldFormData.maxValue}
            onMinValueChange={(val) => handleFieldChange("minValue", val)}
            onMaxValueChange={(val) => handleFieldChange("maxValue", val)}
            maxValueError={validationErrors.maxValue}
          />
        );

      case "slug":
        return (
          <SlugFieldOptionsEditor
            value={fieldFormData.slugOptions}
            onChange={(opts) => handleFieldChange("slugOptions", opts)}
            minValue={fieldFormData.minValue}
            maxValue={fieldFormData.maxValue}
            onMinValueChange={(val) => handleFieldChange("minValue", val)}
            onMaxValueChange={(val) => handleFieldChange("maxValue", val)}
            maxValueError={validationErrors.maxValue}
          />
        );
      case "coordinates":
        return (
          <CoordinatesFieldOptionsEditor
            fieldName={fieldFormData.label || fieldFormData.name}
            value={fieldFormData.coordinatesOptions}
            onChange={(opts) => handleFieldChange("coordinatesOptions", opts)}
          />
        );
      default:
        return (
          <Text size="xs" c="dimmed">
            This field type uses built-in validation.
          </Text>
        );
    }
  };

  const renderFieldForm = () => {
    return (
      <Stack gap="md">
        {/* Basic Fields - Always Visible */}
        <TextInput
          label="Field Name"
          placeholder="e.g., email, phone, message"
          value={fieldFormData.name}
          onChange={(e) => handleFieldChange("name", e.target.value)}
          error={validationErrors.name}
          description="Technical name (no spaces, use underscores)"
          required
          withAsterisk
          size="sm"
        />

        <TextInput
          label="Field Label"
          placeholder="Label shown to users"
          value={fieldFormData.label}
          onChange={(e) => handleFieldChange("label", e.target.value)}
          error={validationErrors.label}
          required
          withAsterisk
          size="sm"
        />

        <FormFieldTypeSelector
          label="Field Type"
          value={fieldFormData.fieldType?.toString() || null}
          onChange={handleFieldTypeChange}
          error={validationErrors.fieldType}
          required
          size="sm"
        />

        <Switch
          label="Required Field"
          description="Users must fill in this field"
          checked={fieldFormData.required}
          onChange={(e) =>
            handleFieldChange("required", e.currentTarget.checked)
          }
          size="sm"
        />

        {/* Accordion for additional settings */}
        <Accordion
          variant="separated"
          radius="sm"
          defaultValue={
            fieldCategory !== "none" && fieldCategory !== "minMax"
              ? "typeOptions"
              : null
          }
        >
          {/* Type-Specific Options */}
          {fieldCategory !== "none" && (
            <Accordion.Item value="typeOptions">
              <Accordion.Control>
                <Text size="sm" fw={500}>
                  {fieldCategory === "minMax" ? "Validation" : "Field Options"}
                </Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="sm">
                  {renderTypeSpecificOptions()}

                  <Textarea
                    label="Custom Error Message"
                    placeholder="Please enter a valid value"
                    value={fieldFormData.customErrorMessage}
                    onChange={(e) =>
                      handleFieldChange("customErrorMessage", e.target.value)
                    }
                    description="Shown when validation fails (optional)"
                    minRows={2}
                    maxRows={3}
                    autosize
                    size="sm"
                  />
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          )}

          {/* Uniqueness Settings */}
          <Accordion.Item value="uniqueness">
            <Accordion.Control>
              <Text size="sm" fw={500}>
                Uniqueness
              </Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="sm">
                <Text size="xs" c="dimmed">
                  Prevent duplicate submissions by requiring unique values.
                </Text>

                <NumberInput
                  label="Max Duplicate Values"
                  placeholder="Unlimited"
                  value={fieldFormData.maxUniqueValues ?? ""}
                  onChange={(value) =>
                    handleFieldChange(
                      "maxUniqueValues",
                      value === "" ? null : value
                    )
                  }
                  description="Set to 1 for fully unique values"
                  min={1}
                  size="sm"
                />

                <Textarea
                  label="Duplicate Message"
                  placeholder="This value has already been submitted"
                  value={fieldFormData.messageOnUniqueness}
                  onChange={(e) =>
                    handleFieldChange("messageOnUniqueness", e.target.value)
                  }
                  description="Shown when duplicate is detected"
                  minRows={2}
                  maxRows={3}
                  autosize
                  size="sm"
                />
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>

          {/* Advanced Settings */}
          <Accordion.Item value="advanced">
            <Accordion.Control>
              <Text size="sm" fw={500}>
                Advanced
              </Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="sm">
                <TextInput
                  label="Default Value"
                  placeholder="Pre-filled value (optional)"
                  value={fieldFormData.defaultValue}
                  onChange={(e) =>
                    handleFieldChange("defaultValue", e.target.value)
                  }
                  size="sm"
                />

                <Switch
                  label="Hidden field"
                  description="Not visible to users but included in submissions"
                  checked={fieldFormData.isHidden}
                  onChange={(e) =>
                    handleFieldChange("isHidden", e.currentTarget.checked)
                  }
                  size="sm"
                />

                {showReplyTo && (
                  <Switch
                    label="Use as Reply-To address"
                    description="Use this email as reply-to for notifications"
                    checked={fieldFormData.isReplyTo}
                    onChange={(e) =>
                      handleFieldChange("isReplyTo", e.currentTarget.checked)
                    }
                    size="sm"
                  />
                )}

                {showGibberishCheck && (
                  <GibberishCheckSelector
                    value={fieldFormData.gibberishCheck}
                    onChange={(level) =>
                      handleFieldChange("gibberishCheck", level)
                    }
                    label="Gibberish Detection Override"
                    description="Override form-level gibberish detection for this field"
                    isFieldLevel
                    size="sm"
                  />
                )}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>

        {/* Action Buttons */}
        <Group justify="flex-end" gap="sm" mt="md">
          <AppButton variant="secondary" size="sm" onClick={handleCancelEdit}>
            Cancel
          </AppButton>
          <AppButton
            variant="primary"
            size="sm"
            iconName="Check"
            onClick={handleSaveField}
            inProgress={isCreating || isUpdating}
          >
            Save Field
          </AppButton>
        </Group>
      </Stack>
    );
  };

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title="Form Fields"
      description={form?.name || "Loading..."}
      position="right"
      size="lg"
      closable={!isCreating && !isUpdating}
      loading={isCreating || isUpdating}
      errors={lastError}
      leftActions={
        effectiveFormId && canEditFields
          ? [
              {
                label: "Edit Form",
                variant: "tertiary",
                iconName: "Settings",
                onClick: handleEditForm,
              },
            ]
          : undefined
      }
      actionType="close"
    >
      <Stack gap="md" h="100%">
        {/* Add Field Button */}
        {canEditFields && (
          <AppButton
            fullWidth
            variant="primary"
            iconName="Plus"
            onClick={handleAddField}
            disabled={isAddingNew || editingFieldId !== null || loading}
          >
            Add Field
          </AppButton>
        )}

        {/* New Field Form */}
        {isAddingNew && (
          <Paper p="md" withBorder>
            <Text size="sm" fw={500} mb="md">
              New Field
            </Text>
            {renderFieldForm()}
          </Paper>
        )}

        {/* Fields List */}
        <ScrollArea flex={1} offsetScrollbars>
          {loading && localFields.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="xl">
              Loading fields...
            </Text>
          ) : localFields.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="xl">
              No fields yet. Add your first field above.
            </Text>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable
                droppableId="form-fields"
                renderClone={(provided, _ /*snapshot*/, rubric) => {
                  const field = localFields[rubric.source.index];
                  return (
                    <Paper
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      p="md"
                      withBorder
                      style={{
                        ...provided.draggableProps.style,
                        backgroundColor: "var(--mantine-color-gray-0)",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        cursor: "grabbing",
                      }}
                    >
                      {renderFieldSummary(field)}
                    </Paper>
                  );
                }}
              >
                {(provided) => (
                  <Stack
                    gap="xs"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {localFields.map((field, index) => (
                      <Draggable
                        key={field.id}
                        draggableId={field.id || `field-${index}`}
                        index={index}
                        isDragDisabled={
                          editingFieldId === field.id ||
                          isAddingNew ||
                          savingFieldId !== null
                        }
                      >
                        {(provided, snapshot) => (
                          <Paper
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            p="md"
                            withBorder
                            style={{
                              ...provided.draggableProps.style,
                              backgroundColor: snapshot.isDragging
                                ? "var(--mantine-color-gray-1)"
                                : savingFieldId === field.id
                                ? "var(--mantine-color-gray-1)"
                                : "white",
                              opacity: snapshot.isDragging
                                ? 0.5
                                : savingFieldId === field.id
                                ? 0.7
                                : 1,
                              cursor: snapshot.isDragging
                                ? "grabbing"
                                : "default",
                              transition: snapshot.isDragging
                                ? "none"
                                : "opacity 0.2s, background-color 0.2s",
                            }}
                          >
                            {editingFieldId === field.id ? (
                              <Stack gap="xs">
                                <Text size="sm" fw={500}>
                                  Edit Field
                                </Text>
                                {renderFieldForm()}
                              </Stack>
                            ) : (
                              renderFieldSummary(field)
                            )}
                          </Paper>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Stack>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </ScrollArea>
      </Stack>
    </AppDrawer>
  );
};
